// src/components/dashboard/EnhancedCharts.js
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658'];

// Revenue Trend Chart Component
export const RevenueTrendChart = ({ data = [] }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-300">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Tendance des Revenus</h3>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="month" stroke="#666" />
          <YAxis stroke="#666" />
          <Tooltip 
            formatter={(value) => [`${value}€`, 'Revenus']}
            contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}
          />
          <Area type="monotone" dataKey="revenue" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// User Engagement Chart Component
export const UserEngagementChart = ({ data = [] }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-300">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Engagement des Utilisateurs</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" stroke="#666" />
          <YAxis stroke="#666" />
          <Tooltip 
            contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}
          />
          <Legend />
          <Line type="monotone" dataKey="activeUsers" stroke="#8884d8" name="Utilisateurs actifs" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
          <Line type="monotone" dataKey="newUsers" stroke="#82ca9d" name="Nouveaux utilisateurs" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Performance Metrics Chart Component
export const PerformanceMetricsChart = ({ data = [] }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-300">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Métriques de Performance</h3>
      <ResponsiveContainer width="100%" height={300}>
        <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
          <PolarGrid stroke="#f0f0f0" />
          <PolarAngleAxis dataKey="subject" stroke="#666" />
          <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#666" />
          <Radar
            name="Métriques"
            dataKey="value"
            stroke="#8884d8"
            fill="#8884d8"
            fillOpacity={0.6}
          />
          <Tooltip 
            contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Comparative Analytics Chart Component
export const ComparativeAnalyticsChart = ({ currentPeriod = [], previousPeriod = [] }) => {
  // Combine data for comparison
  const combinedData = currentPeriod.map((item, index) => ({
    name: item.name,
    current: item.value,
    previous: previousPeriod[index] ? previousPeriod[index].value : 0
  }));

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-300">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Analyse Comparative</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={combinedData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" stroke="#666" />
          <YAxis stroke="#666" />
          <Tooltip 
            contentStyle={{ borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}
          />
          <Legend />
          <Bar dataKey="current" fill="#8884d8" name="Période actuelle" radius={[4, 4, 0, 0]} />
          <Bar dataKey="previous" fill="#82ca9d" name="Période précédente" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default {
  RevenueTrendChart,
  UserEngagementChart,
  PerformanceMetricsChart,
  ComparativeAnalyticsChart
};